import {
  createSlice,
  createAsyncThunk,
  createSelector,
} from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import { dashboardAPI, rootAPI } from '../axiosConfig';
import { permissionRoleMapping } from '../app/App.constants';
import { checkIfAdminUser } from '../utils/utils';

export const LOGIN_TOKEN_COOKIE_NAME = 'login_token';

const setCookie = (cName, cValue, expireTime) => {
  // eslint-disable-next-line no-undef
  document.cookie = `${cName}=${cValue};max-age=${expireTime};path=/;`;
};

const deleteCookie = (cname) => {
  // eslint-disable-next-line no-undef
  document.cookie = `${cname}=;path=/;expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
};

export const googleLogin = createAsyncThunk(
  'auth/google',
  async ({ token }, { rejectWithValue }) => {
    try {
      const { data: signInData } = await dashboardAPI.post('auth/signin', '', {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${token}`,
        },
        noHeader: true,
        bypassAutoLogout: true,
      });

      if (signInData.status === 'OK') return signInData;

      return rejectWithValue(signInData);
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const logoutUser = createAsyncThunk('auth/logoutUser', async () => {
  const response = await dashboardAPI.delete('auth/signout', {
    bypassAutoLogout: true,
  });
  const data = await response.data;
  return data;
});

export const userDetail = createAsyncThunk('auth/user/me', async () => {
  const response = await dashboardAPI
    .get('auth/user/me', { withCredentials: true })
    .catch((error) => error && error.response && error.response);
  const data = await response.data;
  return data;
});

const initialAuthenticationState = {
  isAuthenticated: false,
  userRoles: [],
  username: '',
  email: '',
  name: '',

  isLoggingInSSO: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState: initialAuthenticationState,
  reducers: {
    login(state) {
      state.isAuthenticated = true;
    },
    logout(state) {
      state.isAuthenticated = false;
    },
  },
  extraReducers: {
    [googleLogin.fulfilled]: (state, action) => {
      if (
        !action.payload ||
        !action.payload.data ||
        action.payload.status !== 'OK'
      ) {
        return;
      }

      const loginToken =
        action.payload.data && action.payload.data.refresh_token;
      const expiredIn = action.payload.data && action.payload.data.expired_in;
      setCookie(LOGIN_TOKEN_COOKIE_NAME, loginToken, expiredIn);

      (state.isLoggingInSSO = false), (state.isAuthenticated = true);
    },
    [googleLogin.rejected]: (state) => {
      deleteCookie(LOGIN_TOKEN_COOKIE_NAME);

      state.isLoggingInSSO = false;
      state.isAuthenticated = false;
    },
    [googleLogin.pending]: (state) => {
      state.isLoggingInSSO = true;
    },
    [logoutUser.fulfilled]: (state) => {
      deleteCookie(LOGIN_TOKEN_COOKIE_NAME);
      state.isAuthenticated = false;
    },
    // The session is gone locally regardless of what the signout call returned.
    [logoutUser.rejected]: (state) => {
      deleteCookie(LOGIN_TOKEN_COOKIE_NAME);
      state.isAuthenticated = false;
    },
    [userDetail.fulfilled]: (state, action) => {
      if (
        !action.payload ||
        !action.payload.data ||
        action.payload.status !== 'OK'
      ) {
        return;
      }

      state.isAuthenticated = true;
      state.userRoles = action.payload.data && action.payload.data.roles;
      state.username = action.payload.data && action.payload.data.username;
      state.email = action.payload.data && action.payload.data.email;
      state.name = action.payload.data && action.payload.data.name;
    },
  },
});

export const authActions = authSlice.actions;

export const selectLoggedInUser = (state) => state.auth.username;
export const selectLoggedInUserEmail = (state) => state.auth.email;
export const selectUserRoles = (state) => state.auth.userRoles;
export const selectIsUserAdmin = (state) =>
  checkIfAdminUser(state.auth.userRoles);

export const selectUserPermissions = createSelector(
  selectUserRoles,
  (userRoles) =>
    Object.entries(permissionRoleMapping).reduce(
      (prev, [key, allowedRoles]) => ({
        ...prev,
        [key]: userRoles.some(({ authority }) =>
          allowedRoles.includes(authority),
        ),
      }),
      {},
    ),
);

export const selectIsLoggingInSSO = (state) => state.auth.isLoggingInSSO;

export const usePermissions = () => useSelector(selectUserPermissions);

export default authSlice.reducer;
